import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Home as HomeIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";
import { getRowHouses, createRowHouse, updateRowHouse, deleteRowHouse } from '../../services/master';

const Rowhouse = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [rowHouseData, setRowHouseData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRowHouseForDelete, setSelectedRowHouseForDelete] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [editingRowHouse, setEditingRowHouse] = useState(null);
  const [newRowHouse, setNewRowHouse] = useState({
    rowHouse: '',
    status: 'Active',
  });

  // Fetch row houses
  const fetchRowHouses = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const rowHouses = await getRowHouses();
      setRowHouseData(rowHouses);
    } catch (err) {
      setApiError('Failed to load row houses.');
      setRowHouseData([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRowHouses();
  }, []);

  // Filter logic
  const getFilteredRowHouses = () => {
    return rowHouseData.filter((rh) => {
      const matchesSearch =
        rh.rowHouse.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || rh.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const filteredRowHouses = getFilteredRowHouses();

  // Pagination
  const paginatedRowHouses = filteredRowHouses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Validation
  const validateForm = () => {
    const err = {};
    if (!newRowHouse.rowHouse.trim()) err.rowHouse = 'Row House is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return rowHouseData.some(
      (rh) =>
        rh.rowHouse.toLowerCase() === newRowHouse.rowHouse.trim().toLowerCase() &&
        rh.id !== editingRowHouse?.id,
    );
  };

  // Handlers
  const handleOpenModal = () => {
    setEditingRowHouse(null);
    setNewRowHouse({
      rowHouse: '',
      status: 'Active',
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingRowHouse(null);
    setNewRowHouse({
      rowHouse: '',
      status: 'Active',
    });
    setErrors({});
  };

  const handleEdit = (rowHouse) => {
    setEditingRowHouse(rowHouse);
    setNewRowHouse({
      rowHouse: rowHouse.rowHouse,
      status: rowHouse.status,
    });
    setErrors({});
    setOpenModal(true);
  };

  const handleDeleteClick = (rowHouse) => {
    setSelectedRowHouseForDelete(rowHouse);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRowHouseForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      await deleteRowHouse(selectedRowHouseForDelete.id);
      setAlertConfig({ title: 'Success', message: 'Row house deleted successfully!' });
      setAlertOpen(true);
      fetchRowHouses(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete row house.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedRowHouseForDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ rowHouse: 'Row house already exists' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'rowHouse',
        details: {
          rowHouse: newRowHouse.rowHouse.trim(),
        },
        status: newRowHouse.status === 'Active',
      };

      if (editingRowHouse) {
        await updateRowHouse(editingRowHouse.id, payload);
      } else {
        await createRowHouse(payload);
      }

      setAlertConfig({
        title: 'Success',
        message: editingRowHouse ? 'Row house updated successfully!' : 'Row house added successfully!'
      });
      setAlertOpen(true);
      handleCloseModal();
      fetchRowHouses(true);
    } catch (err) {
      setErrors({ rowHouse: 'Failed to save row house.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
    fetchRowHouses(true);
  };

  const handleChange = (field, value) => {
    setNewRowHouse((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Row House Master
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchRowHouses()}
            disabled={isLoading}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenModal}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flex: { xs: 1, sm: 'none' } }}>
              Add Row House
            </Button>
          )}
        </Box>
      </Box>

      {/* Modal alert/confirm states */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the row house <strong>{selectedRowHouseForDelete?.rowHouse}</strong>? This action cannot be undone.
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchRowHouses()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Search & Filter Section */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Row House..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filteredRowHouses.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} row houses matching your criteria.` : 'No row houses found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleReset}
                  sx={{ textTransform: 'none', height: '56px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading && !rowHouseData.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Row House</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRowHouses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'No row houses found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRowHouses.map((rowHouse, index) => (
                    <TableRow key={rowHouse.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{rowHouse.rowHouse}</TableCell>
                      <TableCell>
                        <Chip
                          label={rowHouse.status}
                          color={rowHouse.status === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {canWrite && (
                          <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleEdit(rowHouse)} disabled={isLoading}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        {canWrite && (
                          <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteClick(rowHouse)} disabled={isLoading}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredRowHouses.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRowHouse ? 'Edit Row House' : 'Add New Row House'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Row House"
                  value={newRowHouse.rowHouse}
                  onChange={(e) => handleChange('rowHouse', e.target.value)}
                  error={!!errors.rowHouse}
                  helperText={errors.rowHouse}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newRowHouse.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isLoading}>
              {editingRowHouse ? 'Update' : 'Add'} Row House
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Rowhouse;
